import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import flowBpmService from "../../services/flowBpmService";

const FlowBpmPage = () => {
  const { t } = useTranslation();
  const [processes, setProcesses] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [processData, taskData] = await Promise.all([
        flowBpmService.getProcessDefinitions(),
        flowBpmService.getTasks(),
      ]);
      setProcesses(Array.isArray(processData) ? processData : []);
      setTasks(Array.isArray(taskData) ? taskData : []);
    } catch (loadError) {
      setError(loadError.message || t("flowBpm.errors.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  const startAiSalesOrderProcess = async () => {
    setStarting(true);
    setError(null);
    try {
      await flowBpmService.startProcess("aiSalesOrderCreation", {
        businessKey: `AI-SALES-${Date.now()}`,
        customerAccount: "CLI-DEMO",
        customerReference: "PO-DEMO-IA",
        aiConfidence: 0.72,
        hasUnresolvedItems: true,
        source: "AI_EMAIL_ORDER",
      });
      await load();
    } catch (startError) {
      setError(startError.message || t("flowBpm.errors.start"));
    } finally {
      setStarting(false);
    }
  };

  const completeTask = async (task) => {
    setCompletingTaskId(task.id);
    setError(null);
    try {
      const variables = variablesForTask(task);
      await flowBpmService.completeTask(task.id, variables);
      await load();
    } catch (completeError) {
      setError(completeError.message || t("flowBpm.errors.complete"));
    } finally {
      setCompletingTaskId(null);
    }
  };

  const variablesForTask = (task) => {
    if (task.taskDefinitionKey === "reviewAiExtraction") {
      return { reviewApproved: true };
    }
    if (task.taskDefinitionKey === "approveSalesOrder") {
      return { managerApproved: true, managerNotes: "Aprobado desde IDAX" };
    }
    return {};
  };

  return (
    <section className="flow-bpm-page">
      <header className="flow-bpm-header">
        <div>
          <h1>{t("flowBpm.title")}</h1>
          <p>{t("flowBpm.subtitle")}</p>
        </div>
        <div className="flow-bpm-actions">
          <button type="button" className="btn btn-default" onClick={load} disabled={loading}>
            <i className="fa fa-refresh" aria-hidden="true"></i>
            <span>{t("flowBpm.refresh")}</span>
          </button>
          <button type="button" className="btn btn-primary" onClick={startAiSalesOrderProcess} disabled={starting}>
            <i className="fa fa-play" aria-hidden="true"></i>
            <span>{starting ? t("common.processing") : t("flowBpm.startAiOrder")}</span>
          </button>
        </div>
      </header>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="flow-bpm-grid">
        <section className="flow-bpm-panel">
          <h2>{t("flowBpm.processes")}</h2>
          {loading ? (
            <p>{t("common.loading")}</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>{t("flowBpm.fields.name")}</th>
                  <th>{t("flowBpm.fields.key")}</th>
                  <th>{t("flowBpm.fields.version")}</th>
                </tr>
              </thead>
              <tbody>
                {processes.map((process) => (
                  <tr key={process.id}>
                    <td>{process.name || process.key}</td>
                    <td>{process.key}</td>
                    <td>{process.version}</td>
                  </tr>
                ))}
                {!processes.length && (
                  <tr>
                    <td colSpan="3">{t("flowBpm.emptyProcesses")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>

        <section className="flow-bpm-panel">
          <h2>{t("flowBpm.tasks")}</h2>
          {loading ? (
            <p>{t("common.loading")}</p>
          ) : (
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>{t("flowBpm.fields.task")}</th>
                  <th>{t("flowBpm.fields.instance")}</th>
                  <th>{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <strong>{task.name}</strong>
                      <small>{task.taskDefinitionKey}</small>
                    </td>
                    <td>{task.processInstanceId}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => completeTask(task)}
                        disabled={completingTaskId === task.id}
                      >
                        <i className="fa fa-check" aria-hidden="true"></i>
                        <span>{t("flowBpm.complete")}</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {!tasks.length && (
                  <tr>
                    <td colSpan="3">{t("flowBpm.emptyTasks")}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </section>
  );
};

export default FlowBpmPage;

